import { Helmet } from '@dr.pogodin/react-helmet';
import { type ReactElement } from 'react';
import { useLocation } from 'react-router';
import { useJsonLdSiteUrl } from '@/lib/json-ld-site-url-context';

/**
 * Emits a minimal WebPage JSON-LD block for pages that don't render their own
 * structured data. Currently covers /privacy and /terms (compliance-managed
 * files that cannot be edited directly). All other pages supply richer
 * page-specific JSON-LD via their own <Helmet> — this component is a no-op
 * for them since multiple JSON-LD blocks per page are valid and harmless.
 */

const PAGES_NEEDING_FALLBACK = new Set(['/privacy', '/terms']);

const PAGE_META: Record<string, { name: string; description: string; type: string }> = {
  '/privacy': {
    name: 'Privacy Policy | Sand and Sun Travels',
    description:
      'Privacy policy for Sand and Sun Travels LLC — how we collect, use, and protect your personal information.',
    type: 'WebPage',
  },
  '/terms': {
    name: 'Terms of Use | Sand and Sun Travels',
    description:
      'Terms of use for sandandsuntravels.com — the rules and conditions governing your use of our website and services.',
    type: 'WebPage',
  },
};

export default function WebPageFallbackJsonLd(): ReactElement | null {
  const location = useLocation();
  const siteUrl: string = useJsonLdSiteUrl();

  if (!PAGES_NEEDING_FALLBACK.has(location.pathname) || !siteUrl) {
    return null;
  }

  const meta = PAGE_META[location.pathname];
  const pageUrl = `${siteUrl}${location.pathname}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': meta.type,
    '@id': `${pageUrl}#webpage`,
    name: meta.name,
    description: meta.description,
    url: pageUrl,
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#organization` },
    inLanguage: 'en-US',
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(jsonLd).replace(/</g, '\\u003c')}
      </script>
    </Helmet>
  );
}
